import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

import { NextPage } from 'next';
import { AppProps } from 'next/app';

import { AppProviders, AuthenticationWrapper } from '@explorer/containers';
import { getRuntimeEnv } from '@explorer/helpers';
import { AuthProvider } from '@explorer/stores';
import { LDProvider } from 'launchdarkly-react-client-sdk';

import '../public/css/main.css';
import '../public/css/react-circular-progressbar.css';
import { FeatureFlags } from '../types/FeatureFlags';

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<
  P,
  IP
> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp: React.FunctionComponent<any> = ({
  Component,
  pageProps,
}: AppPropsWithLayout) => {
  const getLayout = Component.getLayout || ((page: ReactNode) => page);
  const [ldClientSideID, setLdClientSideID] = useState('');

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentNode!.removeChild(jssStyles);
    }

    // Get LaunchDarkly client ID at runtime
    setLdClientSideID(getRuntimeEnv('LAUNCH_DARKLY_CLIENT_ID', ''));

    // Expose the app version
    if (typeof window !== 'undefined') {
      window.REACT_APP_VERSION = getRuntimeEnv('REACT_APP_VERSION');
    }
  }, []);

  const appContent = !ldClientSideID ? (
    <AppProviders>
      <AuthenticationWrapper>
        {getLayout(<Component {...pageProps} />)}
      </AuthenticationWrapper>
    </AppProviders>
  ) : (
    <LDProvider
      clientSideID={ldClientSideID}
      flags={FeatureFlags}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
      options={{
        allAttributesPrivate: true,
        bootstrap: 'localStorage',
        diagnosticOptOut: true,
        fetchGoals: false,
        sendEvents: false,
      }}
    >
      <AppProviders>
        <AuthenticationWrapper>
          {getLayout(<Component {...pageProps} />)}
        </AuthenticationWrapper>
      </AppProviders>
    </LDProvider>
  );

  return <AuthProvider>{appContent}</AuthProvider>;
};

export default MyApp;
